import {
  Checkbox,
  Grid,
  Stack,
  Typography,
  styled,
  Divider,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import {
  ApiIncrementalityControlGroupType,
  ApiIncrementalityControlRegionPayload,
  ApiChartRankDrection,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import GlassCard from "Components/GlassCard/GlassCard";
import StyledComposedChart from "features/analytics/components/StyledComposedChart";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelectedProject } from "Hooks";
import { useChartData } from "features/analytics";
import { IChart } from "Interfaces";
import {
  CONTROL_GROUP_PREVIEW_TITLE,
  MATCHED_REGIONS_LABEL,
  OVERALL_SIMILARITY_SCORE_LABEL,
  SIMILARITY_BASELINE_DESCRIPTION,
  SIMILARITY_CRITERIA,
} from "../helpers/copy";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import produce from "immer";
import moment from "moment";

const AUTO_MATCH_CHART: IChart = {
  name: "Incrementality Auto Match",
  dimension: [
    ApiDimension.DIMENSION_COUNTRY,
    ApiDimension.DIMENSION_CITY,
    ApiDimension.DIMENSION_CHANNEL,
    ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
  ],
  dateDimension: ApiDimension.DIMENSION_SESSION_START_BY_WEEK, // used to make sure we dont inlcude attr sales bucket date in query
  template: ApiChartTemplate.TREND,
  type: ApiChartType.TABLE,
  attributes: [],
  limit: 5000,
  orderBy: `${ApiMetric.METRIC_RANK} DESC`,
  yAxisKeys: [
    {
      key: "l",
      fields: [
        ApiMetric.METRIC_SESSIONS_COUNT,
        ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
        ApiMetric.METRIC_RANK,
      ],
    },
  ],
  ranks: [
    {
      metric: ApiMetric.METRIC_SESSIONS_COUNT,
      direction: ApiChartRankDrection.DESC,
      weight: 0.6,
    },
    {
      metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
      direction: ApiChartRankDrection.DESC,
      weight: 0.4,
    },
  ],
  displayOptions: {
    noFilters: true,
  },
};

const DATE_OVERRIDE = {
  from: undefined,
  to: undefined,
  lastNDays: 90,
};

function calcRankSimilarity(
  targetRank: number,
  candidateRank: number,
  maxDiff: number,
): number {
  const diff = Math.abs(candidateRank - targetRank);
  if (maxDiff <= 0) return 100;
  return Math.max(0, Math.round((1 - diff / maxDiff) * 100));
}

function distributionSimilarity(
  a: Record<string, number>,
  b: Record<string, number>,
): number {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  if (!keys.size) return 0;
  let l1 = 0;
  keys.forEach((key) => {
    l1 += Math.abs((a[key] || 0) - (b[key] || 0));
  });
  // L1 for probability distributions ranges 0..2
  return Math.max(0, Math.round((1 - l1 / 2) * 100));
}

function normalizeDistribution(
  source: Record<string, number>,
): Record<string, number> {
  const total = Object.values(source).reduce((acc, cur) => acc + cur, 0);
  if (total <= 0) return {};
  return Object.entries(source).reduce<Record<string, number>>(
    (acc, [k, v]) => {
      acc[k] = v / total;
      return acc;
    },
    {},
  );
}

const PreviewGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const MatchedRegionsGridItem = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(2),
  },
}));

const SimilarityScoreGridItem = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

function IncrementalityControlGroupPreview() {
  const { testId } = useParams<{ testId: string }>();
  const numericTestId = useMemo(() => {
    if (!testId || testId === "new") return undefined;
    const parsed = Number(testId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [testId]);
  const isExistingTest = !!numericTestId;
  const { incrementalityTest, setIncrementalityTest, setTrendScore } =
    useIncrementalitySetupContext();
  const selectedRegion = useMemo(
    () =>
      incrementalityTest.testRegionCity && incrementalityTest.testRegionCountry
        ? {
            city: incrementalityTest.testRegionCity,
            country: incrementalityTest.testRegionCountry,
          }
        : null,
    [incrementalityTest.testRegionCity, incrementalityTest.testRegionCountry],
  );
  const autoMatched =
    incrementalityTest.controlGroupType ===
    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED;
  const baselineDateOverride = useMemo(() => {
    if (incrementalityTest.baselineGeneratedAt) {
      const baseline = moment(incrementalityTest.baselineGeneratedAt).utc();
      const end = baseline.clone().subtract(1, "day").endOf("day").toDate();
      const start = baseline
        .clone()
        .subtract(90, "days")
        .utc()
        .startOf("day")
        .toDate();
      return {
        from: start,
        to: end,
        lastNDays: undefined,
      };
    }
    return DATE_OVERRIDE;
  }, [incrementalityTest.baselineGeneratedAt]);
  const { selectedProject } = useSelectedProject();
  const { chartData } = useChartData(
    selectedProject,
    AUTO_MATCH_CHART,
    baselineDateOverride,
    { ignoreModel: true },
  );

  const matchedRows = useMemo(() => {
    if (!autoMatched || !selectedRegion || !chartData?.data?.length) {
      return [];
    }

    const validRows = chartData.data.filter((row) => {
      const city = String(row[ApiDimension.DIMENSION_CITY] || "").trim();
      const country = String(row[ApiDimension.DIMENSION_COUNTRY] || "").trim();
      return !!city && !!country;
    });

    const countryRows = validRows.filter(
      (row) =>
        String(row[ApiDimension.DIMENSION_COUNTRY] || "").trim() ===
        selectedRegion.country,
    );

    const cityProfiles = new Map<
      string,
      {
        weightedRank: number;
        sessions: number;
        byChannel: Record<string, number>;
        byWeek: Record<string, number>;
      }
    >();

    countryRows.forEach((row) => {
      const city = String(row[ApiDimension.DIMENSION_CITY] || "").trim();
      const channel = String(row[ApiDimension.DIMENSION_CHANNEL] || "").trim();
      const week = String(
        row[ApiDimension.DIMENSION_SESSION_START_BY_WEEK] || "",
      ).trim();
      const rank = Number(row[ApiMetric.METRIC_RANK] || 0);
      const sessions = Number(row[ApiMetric.METRIC_SESSIONS_COUNT] || 0);
      const prev = cityProfiles.get(city) || {
        weightedRank: 0,
        sessions: 0,
        byChannel: {},
        byWeek: {},
      };

      const safeSessions = Math.max(sessions, 0);
      if (channel) {
        prev.byChannel[channel] = (prev.byChannel[channel] || 0) + safeSessions;
      }
      if (week) {
        prev.byWeek[week] = (prev.byWeek[week] || 0) + safeSessions;
      }

      if (safeSessions <= 0) {
        cityProfiles.set(city, {
          weightedRank: prev.weightedRank,
          sessions: prev.sessions,
          byChannel: prev.byChannel,
          byWeek: prev.byWeek,
        });
        return;
      }

      cityProfiles.set(city, {
        weightedRank: prev.weightedRank + rank * safeSessions,
        sessions: prev.sessions + safeSessions,
        byChannel: prev.byChannel,
        byWeek: prev.byWeek,
      });
    });

    const cityAverages = Array.from(cityProfiles.entries()).map(
      ([city, agg]) => ({
        city,
        rank: agg.sessions > 0 ? agg.weightedRank / agg.sessions : 0,
        channelMix: normalizeDistribution(agg.byChannel),
        weekMix: normalizeDistribution(agg.byWeek),
      }),
    );

    const target = cityAverages.find(
      (entry) => entry.city === selectedRegion.city,
    );
    if (!target) return [];

    const maxRankDiff = Math.max(
      ...cityAverages.map((entry) => Math.abs(entry.rank - target.rank)),
      0,
    );

    return cityAverages
      .filter((entry) => entry.city !== selectedRegion.city)
      .map((entry) => ({
        name: entry.city,
        similarity: Math.round(
          calcRankSimilarity(target.rank, entry.rank, maxRankDiff) * 0.4 +
            distributionSimilarity(target.channelMix, entry.channelMix) * 0.3 +
            distributionSimilarity(target.weekMix, entry.weekMix) * 0.3,
        ),
      }))
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5);
  }, [autoMatched, chartData?.data, selectedRegion]);

  const [selectedCities, setSelectedCities] = useState<Record<string, boolean>>(
    {},
  );
  const lastRegionKeyRef = useRef<string | null>(null);
  const initializedKeyRef = useRef<string | null>(null);
  const savedControlRegionsRef = useRef<
    ApiIncrementalityControlRegionPayload[] | null
  >(null);
  const snapshotTestIdRef = useRef<number | null>(null);

  // Snapshot control regions from API once per test load (before chart re-init runs).
  useEffect(() => {
    if (!numericTestId) {
      savedControlRegionsRef.current = null;
      snapshotTestIdRef.current = null;
      return;
    }
    if (!incrementalityTest.baselineGeneratedAt) return;

    const regions = incrementalityTest.controlRegions || [];
    if (!regions.length) return;

    if (snapshotTestIdRef.current !== numericTestId) {
      snapshotTestIdRef.current = numericTestId;
      savedControlRegionsRef.current = regions.map((region) => ({ ...region }));
    }
  }, [
    numericTestId,
    incrementalityTest.baselineGeneratedAt,
    incrementalityTest.controlRegions,
  ]);

  const applySelection = useCallback(
    (selection: Record<string, boolean>) => {
      setSelectedCities(selection);

      if (!selectedRegion) return;

      const nextControlRegions = matchedRows
        .filter((row) => selection[row.name])
        .map((row) => ({
          city: row.name,
          country: selectedRegion.country,
        }));

      setIncrementalityTest((prev) => {
        const current = prev.controlRegions || [];
        const sameLength = current.length === nextControlRegions.length;
        const sameItems =
          sameLength &&
          current.every(
            (region, index) =>
              region.city === nextControlRegions[index]?.city &&
              region.country === nextControlRegions[index]?.country,
          );
        if (sameItems) return prev;

        return produce(prev, (draft) => {
          draft.controlRegions = nextControlRegions;
        });
      });
    },
    [matchedRows, selectedRegion, setIncrementalityTest],
  );

  const rowsWithSelection = useMemo(
    () =>
      matchedRows.map((row) => ({
        ...row,
        checked: !!selectedCities[row.name],
      })),
    [matchedRows, selectedCities],
  );

  const trendScore = useMemo(() => {
    const selectedRows = rowsWithSelection.filter((row) => row.checked);
    if (!selectedRows.length) return 0;
    return Math.round(
      selectedRows.reduce((acc, row) => acc + row.similarity, 0) /
        selectedRows.length,
    );
  }, [rowsWithSelection]);

  const trendChartData = useMemo(() => {
    if (!autoMatched || !selectedRegion || !chartData?.data?.length) {
      return [];
    }

    const selectedControlCities = new Set(
      rowsWithSelection.filter((row) => row.checked).map((row) => row.name),
    );
    if (!selectedControlCities.size) {
      return [];
    }

    const weekToTest = new Map<
      string,
      { weightedRank: number; sessions: number }
    >();
    const weekToControl = new Map<
      string,
      { weightedRank: number; sessions: number }
    >();

    chartData.data.forEach((row) => {
      const city = String(row[ApiDimension.DIMENSION_CITY] || "").trim();
      const country = String(row[ApiDimension.DIMENSION_COUNTRY] || "").trim();
      const week = String(
        row[ApiDimension.DIMENSION_SESSION_START_BY_WEEK] || "",
      ).trim();
      const sessions = Number(row[ApiMetric.METRIC_SESSIONS_COUNT] || 0);
      const rank = Number(row[ApiMetric.METRIC_RANK] || 0);

      if (
        !week ||
        country !== selectedRegion.country ||
        !Number.isFinite(sessions) ||
        sessions < 0
      ) {
        return;
      }

      if (city === selectedRegion.city) {
        const prev = weekToTest.get(week) || { weightedRank: 0, sessions: 0 };
        weekToTest.set(week, {
          weightedRank: prev.weightedRank + rank * sessions,
          sessions: prev.sessions + sessions,
        });
      } else if (selectedControlCities.has(city)) {
        const prev = weekToControl.get(week) || {
          weightedRank: 0,
          sessions: 0,
        };
        weekToControl.set(week, {
          weightedRank: prev.weightedRank + rank * sessions,
          sessions: prev.sessions + sessions,
        });
      }
    });

    const weeks = Array.from(
      new Set([...weekToTest.keys(), ...weekToControl.keys()]),
    );
    if (!weeks.length) {
      return [];
    }

    weeks.sort((a, b) => {
      const ad = Date.parse(a);
      const bd = Date.parse(b);
      if (!Number.isNaN(ad) && !Number.isNaN(bd)) return ad - bd;
      return a.localeCompare(b);
    });

    return weeks.map((week) => {
      const test = weekToTest.get(week);
      const control = weekToControl.get(week);
      const testRank =
        test && test.sessions > 0 ? test.weightedRank / test.sessions : 0;
      const controlRank =
        control && control.sessions > 0
          ? control.weightedRank / control.sessions
          : 0;

      // Convert backend rank (0..1-ish) to percentage-like display.
      const testScore = Math.round(testRank * 100);
      const controlScore = Math.round(controlRank * 100);
      const weeklySimilarity = Math.max(
        0,
        100 - Math.abs(testScore - controlScore),
      );

      return {
        name: week,
        test: testScore,
        control: controlScore,
        similarity: weeklySimilarity,
      };
    });
  }, [autoMatched, chartData?.data, rowsWithSelection, selectedRegion]);

  const similarityTrendData = useMemo(() => {
    if (!trendChartData.length) {
      return [];
    }

    const baselineTestScore =
      trendChartData.reduce((acc, row) => acc + Number(row.test || 0), 0) /
      Math.max(trendChartData.length, 1);

    return trendChartData.map((row) => {
      const testScore = Number(row.test || 0);
      const controlScore = Number(row.control || 0);

      return {
        name: row.name,
        testSimilarity: Math.max(
          0,
          100 - Math.abs(testScore - baselineTestScore),
        ),
        controlSimilarity: Math.max(
          0,
          100 - Math.abs(controlScore - baselineTestScore),
        ),
      };
    });
  }, [trendChartData]);

  useEffect(() => {
    setTrendScore(trendScore);
  }, [trendScore, setTrendScore]);

  // Initialize checkboxes when matched regions are ready.
  useEffect(() => {
    if (!selectedRegion || !matchedRows.length) {
      setSelectedCities({});
      lastRegionKeyRef.current = null;
      initializedKeyRef.current = null;
      return;
    }

    const regionKey = `${selectedRegion.city}|${selectedRegion.country}`;
    const matchedRowsKey = matchedRows
      .map((row) => row.name)
      .sort()
      .join("|");
    const baselineKey = incrementalityTest.baselineGeneratedAt
      ? String(incrementalityTest.baselineGeneratedAt)
      : "none";
    const initKey = `${regionKey}|${matchedRowsKey}|${baselineKey}`;

    const regionChanged =
      lastRegionKeyRef.current !== null &&
      lastRegionKeyRef.current !== regionKey;
    lastRegionKeyRef.current = regionKey;

    if (initializedKeyRef.current === initKey) return;

    const selectAll = () =>
      matchedRows.reduce<Record<string, boolean>>((acc, row) => {
        acc[row.name] = true;
        return acc;
      }, {});

    // New test or user changed test city → select all matched regions.
    if (!isExistingTest || regionChanged) {
      initializedKeyRef.current = initKey;
      applySelection(selectAll());
      return;
    }

    // Saved test: wait for baseline window + API snapshot before applying selection.
    if (!incrementalityTest.baselineGeneratedAt) return;

    const savedRegions = savedControlRegionsRef.current;
    if (!savedRegions?.length) return;

    const savedCities = new Set(
      savedRegions.map((region) => region.city).filter(Boolean) as string[],
    );
    const matchedNames = new Set(matchedRows.map((row) => row.name));
    const canRestoreSaved = [...savedCities].every((city) =>
      matchedNames.has(city),
    );

    initializedKeyRef.current = initKey;

    if (canRestoreSaved) {
      applySelection(
        matchedRows.reduce<Record<string, boolean>>((acc, row) => {
          acc[row.name] = savedCities.has(row.name);
          return acc;
        }, {}),
      );
    } else {
      applySelection(selectAll());
    }
  }, [
    applySelection,
    incrementalityTest.baselineGeneratedAt,
    isExistingTest,
    matchedRows,
    selectedRegion,
  ]);

  return (
    <GlassCard boxProps={{ p: 3 }} noShadow noBackground>
      <Stack>
        <Typography variant="subtitle1" mb={1}>
          {CONTROL_GROUP_PREVIEW_TITLE}
        </Typography>
        <Divider />
        <PreviewGrid container spacing={2}>
          <MatchedRegionsGridItem item xs={12} md={4}>
            <Typography variant="subtitle2" mb={1}>
              {MATCHED_REGIONS_LABEL}
            </Typography>
            <Stack>
              {rowsWithSelection.map((row) => (
                <Stack
                  key={row.name}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Checkbox
                      checked={row.checked}
                      onChange={(e) =>
                        applySelection({
                          ...selectedCities,
                          [row.name]: e.target.checked,
                        })
                      }
                      size="small"
                      sx={{ pl: 0 }}
                    />
                    <Typography variant="body2">{row.name}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Similarity {row.similarity}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </MatchedRegionsGridItem>
          <SimilarityScoreGridItem item xs={12} md={3}>
            <Typography variant="subtitle2" mb={2}>
              {OVERALL_SIMILARITY_SCORE_LABEL}
            </Typography>
            <Typography variant="h1" color="primary.main" gutterBottom>
              {trendScore}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {SIMILARITY_BASELINE_DESCRIPTION}
            </Typography>
            <Stack spacing={0.5} mt={1}>
              {SIMILARITY_CRITERIA.map((criteria) => (
                <Stack
                  key={criteria}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Check
                    sx={{
                      fontSize: 14,
                      color: "success.main",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {criteria}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </SimilarityScoreGridItem>
          <Grid item xs={12} md={5}>
            <Stack sx={{ height: 220 }}>
              <StyledComposedChart
                data={similarityTrendData}
                noReOrder
                showTooltip
                metricsOrder={["testSimilarity", "controlSimilarity"]}
                yAxisKeys={[
                  {
                    key: "trend",
                    fields: ["testSimilarity", "controlSimilarity"],
                    chart: {
                      testSimilarity: ApiYAxisChartType.LINE,
                      controlSimilarity: ApiYAxisChartType.LINE,
                    },
                  },
                ]}
              />
            </Stack>
          </Grid>
        </PreviewGrid>
      </Stack>
    </GlassCard>
  );
}

export default IncrementalityControlGroupPreview;
